import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DELETE, GET, httpOk, POST, PUT } from 'helpers/api'
import { notify } from 'helpers/helpers'

function ExternalMenuModifiers({ externalMenuProductId }) {
	const { t } = useTranslation()

	const [groups, setGroups] = useState([])
	const [loaded, setLoaded] = useState(false)
	const [groupForm, setGroupForm] = useState({ name: '', min: 1, max: 1 })
	const [editingGroup, setEditingGroup] = useState(null)
	const [panel, setPanel] = useState(null) // null | 'add' | 'copy'
	const [activeGroupId, setActiveGroupId] = useState(null)
	const [modifierProducts, setModifierProducts] = useState([])
	const [copySources, setCopySources] = useState([])
	const [optionSearch, setOptionSearch] = useState('')
	const [copySearch, setCopySearch] = useState('')

	async function loadGroups() {
		if (!externalMenuProductId) return
		const response = await GET(`/external-menu-product-modifiers/${externalMenuProductId}`, {}, { loader: true })
		if (httpOk(response)) {
			setGroups(response.data?.groups || [])
			setLoaded(true)
		}
	}

	async function loadModifierProducts() {
		const response = await GET('/helper/product-modifiers')
		if (httpOk(response)) setModifierProducts(response.data || [])
	}

	useEffect(() => {
		setLoaded(false)
		setGroups([])
		setGroupForm({ name: '', min: 1, max: 1 })
		setEditingGroup(null)
		setPanel(null)
		setCopySources([])
		setActiveGroupId(null)
		if (externalMenuProductId) {
			loadGroups()
		}
	}, [externalMenuProductId]) // eslint-disable-line react-hooks/exhaustive-deps

	const filteredModifierProducts = useMemo(() => {
		const q = optionSearch.trim().toLowerCase()
		if (!q) return modifierProducts
		return modifierProducts.filter((item) => String(item.name || '').toLowerCase().includes(q))
	}, [modifierProducts, optionSearch])

	const filteredCopySources = useMemo(() => {
		const q = copySearch.trim().toLowerCase()
		if (!q) return copySources
		return copySources.filter((item) => String(item.name || '').toLowerCase().includes(q))
	}, [copySources, copySearch])

	function applyGroups(nextGroups) {
		setGroups(nextGroups || [])
	}

	async function createGroup() {
		const name = groupForm.name?.trim()
		if (!name) {
			notify(t('name2'))
			return
		}
		const min = Number(groupForm.min || 0)
		const max = Number(groupForm.max || 1)
		if (max < min) {
			notify(`${t('max')} >= ${t('min')}`)
			return
		}

		const response = await POST('/external-menu-product-modifier-groups', {
			external_menu_product_id: externalMenuProductId,
			name,
			min,
			max,
		}, { loader: true })

		if (httpOk(response)) {
			applyGroups(response.data.groups)
			setGroupForm({ name: '', min: 1, max: 1 })
		}
	}

	async function saveGroup() {
		if (!editingGroup?.id) return
		const name = editingGroup.name?.trim()
		if (!name) {
			notify(t('name2'))
			return
		}
		const min = Number(editingGroup.min || 0)
		const max = Number(editingGroup.max || 1)
		if (max < min) {
			notify(`${t('max')} >= ${t('min')}`)
			return
		}

		const response = await PUT('/external-menu-product-modifier-groups', {
			id: editingGroup.id,
			name,
			min,
			max,
		}, { loader: true })

		if (httpOk(response)) {
			applyGroups(response.data.groups)
			setEditingGroup(null)
		}
	}

	async function deleteGroup(group) {
		const response = await DELETE('/external-menu-product-modifier-groups', {
			id: group.id,
		}, { loader: true })
		if (httpOk(response)) {
			applyGroups(response.data.groups)
			if (editingGroup?.id === group.id) setEditingGroup(null)
		}
	}

	async function openAddOption(group) {
		setActiveGroupId(group.id)
		setOptionSearch('')
		setPanel('add')
		if (!modifierProducts.length) {
			await loadModifierProducts()
		}
	}

	function isOptionAlreadyInGroup(productId) {
		const group = groups.find((g) => g.id === activeGroupId)
		if (!group) return false
		return !!(group.modifiers || []).find((m) => Number(m.product_id) === Number(productId))
	}

	async function addOption(product) {
		if (isOptionAlreadyInGroup(product.id)) {
			notify(t('duplicate') || 'Duplicate')
			return
		}

		const response = await POST('/external-menu-product-modifiers', {
			group_id: activeGroupId,
			product_id: product.id,
			min_amount: 0,
			max_amount: 1,
		}, { loader: true })

		if (httpOk(response)) {
			applyGroups(response.data.groups)
		}
	}

	async function updateOption(modifier, patch) {
		const response = await PUT('/external-menu-product-modifiers', {
			id: modifier.id,
			name: ('name' in patch) ? patch.name : (modifier.name ?? null),
			sale_price: ('sale_price' in patch) ? patch.sale_price : modifier.sale_price,
			min_amount: ('min_amount' in patch) ? patch.min_amount : modifier.min_amount,
			max_amount: ('max_amount' in patch) ? patch.max_amount : modifier.max_amount,
		}, { loader: true })
		if (httpOk(response)) {
			applyGroups(response.data.groups)
		}
	}

	async function deleteOption(modifier) {
		const response = await DELETE('/external-menu-product-modifiers', {
			id: modifier.id,
		}, { loader: true })
		if (httpOk(response)) {
			applyGroups(response.data.groups)
		}
	}

	async function copyFromProduct() {
		const response = await POST('/external-menu-product-modifiers/copy-from-product', {
			external_menu_product_id: externalMenuProductId,
		}, { loader: true, notification: true })
		if (httpOk(response)) {
			applyGroups(response.data.groups)
			setEditingGroup(null)
			setPanel(null)
		}
	}

	async function openCopyFromExternal() {
		setCopySearch('')
		setPanel('copy')
		const response = await GET(
			`/external-menu-product-modifiers/${externalMenuProductId}/copy-sources`,
			{},
			{ loader: true }
		)
		if (httpOk(response)) {
			setCopySources(response.data?.items || [])
		} else {
			setCopySources([])
		}
	}

	async function copyFromExternalProduct(source) {
		if (!source?.id) return
		const response = await POST('/external-menu-product-modifiers/copy-from-external-product', {
			external_menu_product_id: externalMenuProductId,
			source_external_menu_product_id: source.id,
		}, { loader: true, notification: true })
		if (httpOk(response)) {
			applyGroups(response.data.groups)
			setEditingGroup(null)
			setPanel(null)
		}
	}

	if (!externalMenuProductId) {
		return <div className="text-muted p-2">{t('product')}</div>
	}

	if (panel === 'copy') {
		return (
			<div className="pt-1">
				<div className="d-flex justify-content-between align-items-center mb-3 gap-2">
					<div className="fw-600">{t('copy_from_external_menu')}</div>
					<button type="button" className="btn btn-sm btn-outline-warning"
						onClick={() => setPanel(null)}>
						{t('cancel')}
					</button>
				</div>
				<div className="text-muted fz12 mb-3">{t('copy_from_external_menu_hint')}</div>
				<input
					type="text"
					className="form-control mb-3"
					placeholder={t('search')}
					value={copySearch}
					autoFocus
					onChange={(e) => setCopySearch(e.target.value)}
				/>
				<div style={{ maxHeight: 480, overflow: 'auto' }}>
					{filteredCopySources.map((item) => (
						<div key={item.id} className="d-flex justify-content-between py-2 align-items-center"
							style={{ borderBottom: '1px solid var(--dark-border, #eee)' }}>
							<div>
								<div>{item.name}</div>
								<div className="text-muted fz12">
									{!!item.menu_name && <span>{item.menu_name} · </span>}
									{t('modifiers')}: {item.groups_count ?? 0}
								</div>
							</div>
							<button type="button"
								className="btn btn-sm btn-outline-primary"
								onClick={() => copyFromExternalProduct(item)}>
								{t('add')}
							</button>
						</div>
					))}
					{!filteredCopySources.length &&
						<div className="text-muted text-center py-4">{t('list_empty')}</div>
					}
				</div>
			</div>
		)
	}

	if (panel === 'add') {
		return (
			<div className="pt-1">
				<div className="d-flex justify-content-between align-items-center mb-3 gap-2">
					<div className="fw-600">{t('modifiers')}</div>
					<button type="button" className="btn btn-sm btn-outline-warning"
						onClick={() => setPanel(null)}>
						{t('cancel')}
					</button>
				</div>
				<input
					type="text"
					className="form-control mb-3"
					placeholder={t('search')}
					value={optionSearch}
					autoFocus
					onChange={(e) => setOptionSearch(e.target.value)}
				/>
				<div style={{ maxHeight: 480, overflow: 'auto' }}>
					{filteredModifierProducts.map((item) => (
						<div key={item.id} className="d-flex justify-content-between py-2 align-items-center"
							style={{ borderBottom: '1px solid var(--dark-border, #eee)' }}>
							<div>
								{item.name}
								{isOptionAlreadyInGroup(item.id) &&
									<span className="ms-2 text-success">✓</span>
								}
							</div>
							<button type="button"
								className="btn btn-sm btn-outline-primary"
								disabled={isOptionAlreadyInGroup(item.id)}
								onClick={() => addOption(item)}>
								{t('add')}
							</button>
						</div>
					))}
					{!filteredModifierProducts.length &&
						<div className="text-muted text-center py-4">{t('list_empty')}</div>
					}
				</div>
			</div>
		)
	}

	return (
		<>
			<div className="mb-3">
				<div className="d-flex justify-content-between align-items-start gap-2 flex-wrap mb-2">
					<div className="text-muted fz14 pt-1">
						{loaded ? `${t('modifiers')}: ${groups.length}` : '...'}
					</div>
					<div className="d-flex gap-2 flex-wrap">
						<button type="button" className="btn btn-outline-primary btn-sm"
							onClick={copyFromProduct}>
							{t('copy_from_product')}
						</button>
						<button type="button" className="btn btn-outline-secondary btn-sm"
							onClick={openCopyFromExternal}>
							{t('copy_from_external_menu')}
						</button>
					</div>
				</div>
				<div className="text-muted fz12">
					{t('external_modifier_shared_hint')}
				</div>
			</div>

			<div className="d-flex gap-2 mb-3">
				<input
					type="text"
					className="form-control"
					placeholder={t('name2')}
					value={groupForm.name}
					onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
				/>
				<input
					type="number"
					className="form-control"
					style={{ width: 80 }}
					placeholder={t('min')}
					value={groupForm.min}
					onChange={(e) => setGroupForm({ ...groupForm, min: e.target.value.replace(/[^\d]/g, '') })}
				/>
				<input
					type="number"
					className="form-control"
					style={{ width: 80 }}
					placeholder={t('max')}
					value={groupForm.max}
					onChange={(e) => setGroupForm({ ...groupForm, max: e.target.value.replace(/[^\d]/g, '') })}
				/>
				<button type="button" className="btn btn-primary" onClick={createGroup}>
					{t('create')}
				</button>
			</div>

			{editingGroup &&
				<div className="border rounded p-2 mb-3">
					<div className="fw-600 mb-2">{t('edit')}</div>
					<div className="d-flex gap-2">
						<input
							type="text"
							className="form-control"
							value={editingGroup.name ?? ''}
							onChange={(e) => setEditingGroup({ ...editingGroup, name: e.target.value })}
						/>
						<input
							type="number"
							className="form-control"
							style={{ width: 80 }}
							value={editingGroup.min ?? 0}
							onChange={(e) => setEditingGroup({ ...editingGroup, min: e.target.value.replace(/[^\d]/g, '') })}
						/>
						<input
							type="number"
							className="form-control"
							style={{ width: 80 }}
							value={editingGroup.max ?? 1}
							onChange={(e) => setEditingGroup({ ...editingGroup, max: e.target.value.replace(/[^\d]/g, '') })}
						/>
						<button type="button" className="btn btn-primary" onClick={saveGroup}>
							{t('save')}
						</button>
						<button type="button" className="btn btn-outline-warning"
							onClick={() => setEditingGroup(null)}>
							{t('cancel')}
						</button>
					</div>
				</div>
			}

			{groups.length === 0 && loaded &&
				<div className="text-muted mb-2">{t('list_empty')}</div>
			}

			{groups.map((group) => (
				<div key={group.id} className="border rounded mb-3">
					<div className="d-flex justify-content-between align-items-center p-2"
						style={{ background: 'var(--dark-surface-2, #f7f8fa)' }}>
						<div>
							<b>{group.name}</b>
							<span className="ms-2 text-muted">
								{t('min')}: {group.min} / {t('max')}: {group.max}
							</span>
						</div>
						<div className="d-flex gap-1">
							<button type="button" className="btn btn-sm btn-outline-primary"
								onClick={() => openAddOption(group)}>
								{t('add')}
							</button>
							<button type="button" className="btn btn-sm btn-outline-secondary"
								onClick={() => setEditingGroup({
									id: group.id,
									name: group.name,
									min: group.min,
									max: group.max,
								})}>
								{t('edit')}
							</button>
							<button type="button" className="btn btn-sm btn-outline-danger"
								onClick={() => deleteGroup(group)}>
								<i className="uil uil-times"></i>
							</button>
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered mb-0">
							<thead>
								<tr>
									<th style={{ width: '45%' }}>{t('name2')}</th>
									<th style={{ width: '25%' }} className="text-end">{t('sale_price')}</th>
									<th style={{ width: '12%' }} className="text-end">{t('min')}</th>
									<th style={{ width: '12%' }} className="text-end">{t('max')}</th>
									<th style={{ width: '33px', minWidth: '33px', maxWidth: '33px' }}></th>
								</tr>
							</thead>
							<tbody>
								{(group.modifiers || []).map((modifier) => (
									<tr key={modifier.id}>
										<td className="td-input">
											<input
												type="text"
												className="form-control"
												defaultValue={modifier.name || modifier.product_name || ''}
												placeholder={modifier.product_name || t('name2')}
												key={`name-${modifier.id}-${modifier.name ?? modifier.product_name ?? ''}`}
												onKeyDown={(e) => {
													if (e.key !== 'Enter') return
													e.preventDefault()
													e.currentTarget.blur()
												}}
												onBlur={(e) => {
													const value = e.target.value.trim()
													const current = (modifier.name || modifier.product_name || '').trim()
													if (value === current) return
													updateOption(modifier, {
														name: value === '' ? null : value,
													})
												}}
											/>
											{!!modifier.product_name &&
												<div className="text-muted fz12 mt-1">{modifier.product_name}</div>
											}
										</td>
										<td className="td-input">
											<input
												type="text"
												className="form-control text-end"
												defaultValue={modifier.sale_price ?? ''}
												placeholder="0"
												key={`price-${modifier.id}-${modifier.sale_price ?? 'null'}`}
												onKeyDown={(e) => {
													if (e.key !== 'Enter') return
													e.preventDefault()
													e.currentTarget.blur()
												}}
												onBlur={(e) => {
													const value = e.target.value.replace(/[^0-9.]/g, '')
													const next = value === '' ? null : value
													if (String(modifier.sale_price ?? '') === String(next ?? '')) return
													updateOption(modifier, {
														sale_price: next,
													})
												}}
											/>
										</td>
										<td className="td-input">
											<input
												type="number"
												className="form-control text-end"
												defaultValue={modifier.min_amount ?? 0}
												key={`min-${modifier.id}-${modifier.min_amount}`}
												onKeyDown={(e) => {
													if (e.key !== 'Enter') return
													e.preventDefault()
													e.currentTarget.blur()
												}}
												onBlur={(e) => {
													const next = Number(e.target.value || 0)
													if (Number(modifier.min_amount ?? 0) === next) return
													updateOption(modifier, {
														min_amount: next,
													})
												}}
											/>
										</td>
										<td className="td-input">
											<input
												type="number"
												className="form-control text-end"
												defaultValue={modifier.max_amount ?? 1}
												key={`max-${modifier.id}-${modifier.max_amount}`}
												onKeyDown={(e) => {
													if (e.key !== 'Enter') return
													e.preventDefault()
													e.currentTarget.blur()
												}}
												onBlur={(e) => {
													const next = Number(e.target.value || 1)
													if (Number(modifier.max_amount ?? 1) === next) return
													updateOption(modifier, {
														max_amount: next,
													})
												}}
											/>
										</td>
										<td className="text-center" style={{ width: '33px', minWidth: '33px', maxWidth: '33px', padding: '4px' }}>
											<button type="button"
												className="table-action-button table-action-danger-button"
												onClick={() => deleteOption(modifier)}>
												<i className="uil uil-times"></i>
											</button>
										</td>
									</tr>
								))}
								{!(group.modifiers || []).length &&
									<tr>
										<td colSpan={5} className="text-muted text-center">{t('list_empty')}</td>
									</tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			))}
		</>
	)
}

export default ExternalMenuModifiers
